/**
 * @alias confluence-editor-plugins/confluence-editor/src/main/resources/analytics/editor-analytics.js
 */
import window from 'window-or-global';
import { getBuildInfo } from '@confluence/build-info';
import { dangerouslyCreateSafeString } from '@confluence/safe-string';

import { getOverride } from '@confluence/meta';
import { trigger, bind } from '@confluence-classic/confluence-event_DO_NOT_USE';

const PUBLISH_INTERVAL = 60 * 1000;

let analyticsTimer;
let events = {};
let selectionLen = 0;

const bindAnalyticsToEditorCommands = function () {
	clearTimeout(analyticsTimer);
	analyticsTimer = setTimeout(publishAnalytics, PUBLISH_INTERVAL);

	const ucFirst = function (string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	};

	window.tinymce.activeEditor.on('ExecCommand', (e) => {
		const cmd = e.command === 'mceToggleFormat' ? ucFirst(e.value) : e.command;
		addToEventLog(mapToAnalyticsActionName[cmd]);
	});

	window.tinymce.activeEditor.on('KeyPress', () => {
		addToEventLog('keys');
	});

	window.tinymce.activeEditor.on('KeyUp', (e) => {
		if (e.keyCode === 46) {
			addToEventLog('delete', selectionLen);
		} else if (e.keyCode === 8) {
			addToEventLog('backspace', selectionLen);
		}
		selectionLen = window.tinymce.activeEditor.selection.getContent().length;
	});

	window.tinymce.activeEditor.on('MouseUp', () => {
		selectionLen = window.tinymce.activeEditor.selection.getContent().length;
	});

	window.tinymce.activeEditor.on('Paste', (e) => {
		addToEventLog('paste', e.clipboardData.getData('text/plain').length);
	});

	//happens when in-line comment is closed (removed from DOM)
	window.tinymce.activeEditor.on('Remove', () => {
		publishAnalytics();
		clearTimeout(analyticsTimer);
	});
};

// The editor doesn't fire commands for every action, we'll handle the missing cases here

const bindAnalyticsPublishingToWindowUnload = function () {
	// Gets swallowed based on dirty changes and in different situations
	window.addEventListener('beforeunload', () => {
		publishAnalytics();
	});
};

const addToEventLog = function (actionName, length) {
	if (actionName) {
		if (events[actionName]) {
			events[actionName]++;
		} else {
			events[actionName] = 1;
		}
		if (length) {
			const sizeKey = `${actionName}Size`;
			if (events[sizeKey]) {
				events[sizeKey] += length;
			} else {
				events[sizeKey] = length;
			}
		}
	}
};

const publishAnalytics = function () {
	events['contentID'] = getOverride('content-id');
	events['currentVersion'] = getOverride('page-version') || 'null'; //Return null for unpublished drafts
	events['tinymceVersion'] = dangerouslyCreateSafeString(
		`${window.tinymce.majorVersion}.${window.tinymce.minorVersion}`,
	);
	events['frontEndVersion'] = dangerouslyCreateSafeString(getBuildInfo().FRONTEND_VERSION);

	trigger('analytics', { name: 'confluence.editor.action', data: events });

	events = {};
	clearTimeout(analyticsTimer);
	analyticsTimer = setTimeout(publishAnalytics, PUBLISH_INTERVAL);
};

const mapToAnalyticsActionName = {
	Bold: 'bold',
	confCharmap: 'insert.symbol',
	confMonospace: 'monospace',
	confTableRowToggleHeading: 'table.row.toggle.heading',
	confTableColumnToggleHeading: 'table.column.toggle.heading',
	confTableSelectionToggleHighlight: 'table.selection.toggle.highlight',
	ForeColor: 'foreground.color',
	FormatBlock: 'format.block',
	Indent: 'indent',
	InsertHorizontalRule: 'insert.horizontal.rule',
	InsertOrderedList: 'insert.ordered.list',
	InsertUnorderedList: 'insert.unordered.list', // to get the true value of UL we must deduct insert.inline.tasklist from this because tasklist is a subset of UL
	InsertWikiMarkup: 'insert.wiki.markup',
	Italic: 'italic',
	JustifyCenter: 'justify.center',
	JustifyLeft: 'justify.left',
	JustifyRight: 'justify.right',
	mceConfimage: 'insert.files.images',
	mceConflink: 'insert.link',
	mceConfMacroBrowser: 'open.other.macros.browser',
	mceEmotion: 'insert.emoticon',
	mceTableCopyRow: 'table.copy.row',
	mceTableCutRow: 'table.cut.row',
	mceTableDelete: 'table.delete',
	mceTableDeleteCol: 'table.delete.column',
	mceTableDeleteRow: 'table.delete.row',
	mceTableInsertColAfter: 'table.insert.column.after',
	mceTableInsertColBefore: 'table.insert.column.before',
	mceTableInsertRowAfter: 'table.insert.row.after',
	mceTableInsertRowBefore: 'table.insert.row.before',
	mceTableMergeCells: 'table.merge.cells',
	mceTablePasteRowBefore: 'table.paste.row.before',
	mceTableSplitCells: 'table.split.cells',
	mcePageLayoutsToolbar: 'page.layouts.toolbar.toggle',
	mcePageLayoutAddSection: 'page.layouts.toolbar.add.section',
	mcePageLayoutRemoveSection: 'page.layouts.toolbar.remove.section',
	mcePageLayoutMoveSectionDown: 'page.layouts.toolbar.movedown.section',
	mcePageLayoutMoveSectionUp: 'page.layouts.toolbar.moveup.section',
	mcePageLayoutChangeSection: 'page.layouts.toolbar.change.section',
	mceConfShortcutDialog: 'open.help.dialog',
	mceSearchReplaceToolbar: 'search.replace.toolbar.toggle',
	mceConfSearchClose: 'search.replace.toolbar.close',
	mceConfSearch: 'search',
	mceConfReplace: 'replace',
	mceConfReplaceAll: 'replace.all',
	Outdent: 'outdent',
	Redo: 'redo',
	Strikethrough: 'strikethrough',
	subscript: 'subscript',
	superscript: 'superscript',
	Underline: 'underline',
	Undo: 'undo',
	InsertInlineTaskList: 'insert.inline.tasklist',
	InsertInlineTaskListNoToggle: 'insert.inline.tasklist.no.toggle',

	/*
        Audit of All toolbar buttons -> list of commands we don't track yet but could:
            - SelectAll
            - confInsertTable (we track table operations except for inserting new table!)
            - mceTableCutCol (table cut column)
            - mceTableCopyCol (table copy column)
            - mceTablePasteColBefore (table paste column before)
            - mceTableInsertNumberingCol (Insert Table numbering), note this command is triggered again after confTableColumnToggleHeading is triggered which may cause a false positive result
    */
};

export const logAnalytics = addToEventLog;

export default function editorAnalytics() {
	bind('rte-ready', () => {
		bindAnalyticsToEditorCommands();
		bindAnalyticsPublishingToWindowUnload();
	});
}
